<template>
  <div class="row mt-4" v-if="call">
    <div class="col">
      <h5>
        Klic
        <small v-if="call.CallerNumber">{{ call.CallerNumber }}</small>
      </h5>

      <div class="row mb-3">
        <div class="col-md-7">
          <div class="bg-light border rounded-3 p-3 mb-3">
            <div class="row mb-1">
              <div class="col-sm-4 text-muted">Klicatelj</div>
              <div class="col-sm-8">{{ call.CallerNumber }}</div>
            </div>
            <div class="row mb-1">
              <div class="col-sm-4 text-muted">Naša številka</div>
              <div class="col-sm-8">{{ call.TargetNumber }}</div>
            </div>
            <div class="row mb-1">
              <div class="col-sm-4 text-muted">Stanje</div>
              <div class="col-sm-8">
                {{ call.CallState }}
                <span class="text-muted" v-if="call.CallType || call.Personality"
                  >({{ call.CallType }}<span v-if="call.CallType && call.Personality">, </span
                  >{{ call.Personality }})</span
                >
              </div>
            </div>
            <div class="row mb-1">
              <div class="col-sm-4 text-muted">Začetek</div>
              <div
                class="col-sm-8"
                :title="call.CallStart ? $dayjs(call.CallStart).format($DATETIME_FORMAT_DISPLAY) : ''"
              >
                {{ call.CallStart ? $dayjs(call.CallStart).fromNow() : "" }}
              </div>
            </div>
            <div class="row mb-1">
              <div class="col-sm-4 text-muted">Trajanje</div>
              <div class="col-sm-8" :title="call.CallEnd ? $dayjs(call.CallEnd).format($DATETIME_FORMAT_DISPLAY) : ''">
                {{ callDuration }}
              </div>
            </div>
            <div class="row" v-if="call.RedirectNumber">
              <div class="col-sm-4 text-muted">Preusmerjeno na</div>
              <div class="col-sm-8">
                {{ call.RedirectNumber
                }}<span class="text-muted" v-if="call.RedirectReason"> ({{ call.RedirectReason }})</span>
              </div>
            </div>
          </div>

          <!-- a recorded alarm is final (it may already have made an SDN for this customer/object) -->
          <fieldset :disabled="locked">
            <SelectCustomerEntity
              required
              :customerID="call.CustomerID"
              :facilityID="call.FacilityID"
              :canChange="!locked"
              @change="
                (e) => {
                  call.CustomerID = e.customerID
                  call.FacilityID = e.facilityID
                }
              "
            />

            <div class="row mb-3">
              <label class="col-form-label col-sm-4"
                >Ime in priimek klicatelja <span v-if="fieldsRequired">*</span></label
              >
              <div class="col-sm-8">
                <input
                  class="form-control"
                  :class="{ 'is-invalid': fieldsRequired && !hasCallerName }"
                  v-model="call.CallerName"
                />
              </div>
            </div>

            <div class="row mb-3">
              <label class="col-form-label col-sm-4">Razlog / sporočilo <span v-if="fieldsRequired">*</span></label>
              <div class="col-sm-8">
                <textarea
                  class="form-control"
                  :class="{ 'is-invalid': fieldsRequired && !hasNote }"
                  rows="4"
                  v-model="call.Note"
                ></textarea>
              </div>
            </div>

            <!-- What the call turns into - the decision belongs right below the message it is read
               off, and the save button at the bottom is what creates the order it asks for. -->
            <div class="border rounded-3 p-3">
              <h6 class="mb-2">Razlog klica in naloga, ki sledi</h6>

              <div
                class="form-check py-1 rounded"
                :class="outcome === o.value ? 'bg-light' : ''"
                v-for="o in outcomes"
                :key="o.value"
              >
                <input
                  class="form-check-input clickable"
                  type="radio"
                  :id="'outcome-' + o.value"
                  :value="o.value"
                  v-model="outcome"
                />
                <label class="form-check-label clickable" :for="'outcome-' + o.value">{{ o.text }}</label>
              </div>
            </div>
          </fieldset>
        </div>

        <div class="col-md-5">
          <div class="card mb-3" v-if="hasLinkedSdn">
            <div class="card-body">
              <h5 class="card-title">Povezani nalogi</h5>
              <button
                class="btn btn-link p-0 mb-3"
                type="button"
                @click="$router.push({ name: 'ServiceOrder', params: { workOrderId: call.ServiceOrderID } })"
              >
                Odpri SDN {{ call.ServiceOrderNumber }}
              </button>
              <button
                class="btn btn-link p-0 mb-3"
                type="button"
                v-if="call.InternalOrderID"
                @click="$router.push({ name: 'InternalOrder', params: { workOrderId: call.InternalOrderID } })"
              >
                Odpri interni nalog {{ call.InternalOrderNumber }}
              </button>
              <div>
                <button
                  class="btn btn-sm btn-outline-danger"
                  type="button"
                  :disabled="saving || locked"
                  title="Odstrani povezavo in vrni na TBD"
                  @click="unlinkOrderAsync"
                >
                  Odstrani povezavo
                </button>
              </div>
            </div>
          </div>

          <DeviceNumberSearch
            class="mb-3"
            v-else-if="showQuickPick"
            :disabled="saving"
            @select="selectDevice"
            @not-found="onDeviceNotFound"
          >
            <template #hint>
              <small class="text-muted d-block mt-2" v-if="facilityInfo">{{ facilityInfo }}</small>
            </template>
          </DeviceNumberSearch>

          <ServiceOrdersCard
            :key="call.ID"
            v-else-if="sdnPickerOpen"
            class="card mb-3"
            title="SDN na objektu"
            :subTitle="sdnPickerSubTitle"
            :actionTitle="'Poveži'"
            searchable
            :highlight="needsLinkedSdn"
            :statuses="sdnPickerStatuses"
            :itemsPerPage="10"
            :showLinkToMap="false"
            :showFacility="false"
            :showOwner="false"
            :facilityID="call.FacilityID"
            :linkedOrderId="call.ServiceOrderID"
            @select="linkOrderAsync"
          />

          <div class="card mb-3" v-if="needsDeviceSelection">
            <fieldset class="card-body" :disabled="locked">
              <h5 class="card-title">Naprave na objektu</h5>
              <p class="text-muted mb-0" v-if="devices.length === 0">Na objektu ni najdenih naprav.</p>
              <DefrostingDeviceList
                v-else-if="outcome === 'Users'"
                :devices="devices"
                :disabled="saving"
                @toggle="toggleDefrostingAsync"
              />
              <DeviceList v-else :devices="devices" :showSelect="true" :showFacility="false" :showSupplier="false" />
            </fieldset>
          </div>
        </div>
      </div>

      <RequiredWarning v-show="requiredHint">{{ requiredHint }}</RequiredWarning>

      <div class="row mb-5">
        <div class="col-12 text-end">
          <strong>{{ outcomeText(call.CallOutcome) }}</strong>
          <button class="btn btn-outline-secondary me-3" type="button" @click="$router.push({ name: 'Calls' })">
            {{ locked ? "Nazaj" : "Prekliči" }}
          </button>
          <!-- "Alarm" is recorded by one of these two, once - after that the other one goes away and
               the chosen one stays as a (disabled) record of what was done -->
          <template v-if="outcome === 'Alarm'">
            <button
              class="btn btn-lg me-3"
              :class="recordedAlarm === 'AlertSolved' ? 'btn-success' : 'btn-outline-success'"
              type="button"
              v-if="recordedAlarm !== 'Alert'"
              :disabled="saving || locked || !isValid"
              @click="applyAlarmAsync('AlertSolved')"
            >
              Opravljena preverba stanja
            </button>
            <button
              class="btn btn-lg me-3"
              :class="recordedAlarm === 'Alert' ? 'btn-warning' : 'btn-outline-warning'"
              type="button"
              v-if="recordedAlarm !== 'AlertSolved'"
              :disabled="saving || locked || !isValid"
              @click="applyAlarmAsync('Alert')"
            >
              Potreben obisk (nov SDN)
            </button>
          </template>
          <button
            class="btn btn-success btn-lg me-3"
            type="button"
            v-if="outcome !== 'Alarm'"
            @click="saveAsync"
            :disabled="saving || !isValid"
          >
            {{ saveLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import dayjs from "dayjs"
import { useAuth } from "../composition-api/useAuth"
import {
  getCallAsync,
  saveEntityAsync,
  setCallOutcomeAsync,
  unlinkCallOutcomeAsync,
  listEntitiesAsync,
  setDeviceDefrostingAsync,
} from "../api"
import { EMPTY_GUID, CALL_OUTCOMES } from "../utils/constants"
import { setError, setSuccess } from "../components/Notification"
import DeviceNumberSearch from "../components/DeviceNumberSearch.vue"
import ServiceOrdersCard from "../components/ServiceOrdersCard.vue"
import DeviceList from "../components/DeviceList.vue"
import DefrostingDeviceList from "../components/DefrostingDeviceList.vue"

defineOptions({ name: "CallEdit" })

const DEFAULT_OUTCOME = "Tbd"

const route = useRoute()
const router = useRouter()
const { fetchAuthAsync } = useAuth()

const call = ref(null)
const facilityInfo = ref("")
const saving = ref(false)
const outcome = ref("")
const devices = ref([])

const hasFacility = computed(() => !!call.value?.FacilityID && call.value.FacilityID !== EMPTY_GUID)

const hasCustomer = computed(() => !!call.value?.CustomerID && call.value.CustomerID !== EMPTY_GUID)

const hasLinkedSdn = computed(() => !!call.value?.ServiceOrderID && call.value.ServiceOrderID !== EMPTY_GUID)

const needsLinkedSdn = computed(() => outcome.value === "Complaint")

const needsFacility = computed(
  () => needsLinkedSdn.value || outcome.value === "Attention" || outcome.value === "Alarm" || outcome.value === "Users",
)

const needsDeviceSelection = computed(() => outcome.value === "Alarm" || outcome.value === "Users")

// PropertiesJson comes back from the API as the raw JSON string stored in the column - a general
// bag of named call attributes (so far just relatedDeviceIds), parsed here so the device list can
// pre-tick what was saved before (e.g. reopening the call) and so saving can merge into it without
// clobbering whatever other keys future screens may have put there
const savedProperties = computed(() => {
  try {
    return call.value?.PropertiesJson ? JSON.parse(call.value.PropertiesJson) : {}
  } catch {
    return {}
  }
})

const savedDeviceIds = computed(() => savedProperties.value.relatedDeviceIds || [])

const sdnPickerOpen = computed(() => needsLinkedSdn.value || outcome.value === "ExistingSdn")

const EXISTING_SDN_STATUSES = "Triage,Active,OrderMaterial,MaterialOrdered,Offer"

const COMPLAINT_SDN_STATUSES = "Completed,Finalized"

const sdnPickerStatuses = computed(() => {
  if (outcome.value === "ExistingSdn") return EXISTING_SDN_STATUSES
  if (outcome.value === "Complaint") return COMPLAINT_SDN_STATUSES
  return "all"
})

const sdnPickerSubTitle = computed(() =>
  sdnPickerStatuses.value === "all" ? "(vsi statusi)" : `(${sdnPickerStatuses.value.split(",").join(", ")})`,
)

const customerRequired = computed(() => !["Grievance", "Users", "Tbd"].includes(outcome.value))

const hasCallerName = computed(() => !!(call.value?.CallerName || "").trim())
const hasNote = computed(() => !!(call.value?.Note || "").trim())

const fieldsRequired = computed(() => !["WrongNumber", "Users", "Tbd"].includes(outcome.value))

const isValid = computed(() => {
  if (!call.value) return false
  if (needsFacility.value && !hasFacility.value) return false
  if (!fieldsRequired.value) return true
  if (customerRequired.value && !hasCustomer.value) return false
  return hasCallerName.value && hasNote.value
})

// no facility yet - the object (and its customer) is still to be found by the device number
const showQuickPick = computed(() => !hasFacility.value)

const recordedOutcome = computed(() => call.value?.CallOutcome || "")

// the backend keeps AlertSolved / Alert apart, the screen offers them as one "Alarm" choice
const isAlarmOutcome = (value) => !!CALL_OUTCOMES.find((o) => o.value === value)?.alarm

const toScreenOutcome = (value) => (isAlarmOutcome(value) ? "Alarm" : value)

const recordedAlarm = computed(() => (isAlarmOutcome(recordedOutcome.value) ? recordedOutcome.value : ""))

// once an alarm is recorded the call is done - nothing on it can change anymore
const locked = computed(() => !!recordedAlarm.value)

// "Alarm" is never applied by the save button - its own two buttons do that
const pendingOutcome = computed(
  () =>
    !!outcome.value &&
    outcome.value !== "ExistingSdn" &&
    outcome.value !== "Alarm" &&
    outcome.value !== recordedOutcome.value,
)

const canApplyOutcome = computed(() => {
  if (!pendingOutcome.value) return false
  if (needsLinkedSdn.value && !hasLinkedSdn.value) return false
  return true
})

const saveLabel = computed(() => {
  if (!canApplyOutcome.value) return "Shrani"
  switch (outcome.value) {
    case "Other":
      return "Ustvari interni nalog"
    case "Complaint":
      return "Ustvari reklamacijo"
    case "Grievance":
      return "Ustvari opravilo"
    case "Attention":
      return "Ustvari SDN in ga odpri"
    default:
      return "Shrani"
  }
})

const requiredHint = computed(() => {
  if (!call.value) return ""
  if (fieldsRequired.value && customerRequired.value && !hasCustomer.value) return "Izberi stranko."
  if (needsFacility.value && !hasFacility.value) return "Izberi objekt."
  if (fieldsRequired.value) {
    if (!hasCallerName.value) return "Vpiši ime in priimek klicatelja."
    if (!hasNote.value) return "Vpiši razlog / sporočilo."
  }
  if (!pendingOutcome.value) return ""
  if (needsLinkedSdn.value && !hasLinkedSdn.value) return "Poveži SDN na objektu (desno)."
  return ""
})

const outcomes = CALL_OUTCOMES.filter((o) => !o.alarm).map((o) => ({
  value: o.value,
  text: o.hint ? `${o.text} - ${o.hint}` : o.text,
}))

const outcomeText = (value) => CALL_OUTCOMES.find((o) => o.value === value)?.text || value

const loadDevicesAsync = async () => {
  const list = await listEntitiesAsync(fetchAuthAsync, "Devices", `?facilityId=${call.value.FacilityID}`)
  list.forEach((d) => (d.isRelated = savedDeviceIds.value.includes(d.id)))
  devices.value = list
}

watch(
  () => (needsDeviceSelection.value && hasFacility.value ? call.value.FacilityID : null),
  (facilityId) => (facilityId ? loadDevicesAsync() : (devices.value = [])),
)

const callDuration = computed(() => {
  if (!call.value?.CallStart) return ""
  if (!call.value.CallEnd) return "v teku"
  const seconds = dayjs(call.value.CallEnd).diff(dayjs(call.value.CallStart), "second")
  return seconds > 0 ? dayjs.duration(seconds, "seconds").humanize() : ""
})

const loadAsync = async () => {
  call.value = await getCallAsync(fetchAuthAsync, route.params.callId)
  if (!call.value) return
  call.value.CustomerID = call.value.CustomerID || EMPTY_GUID
  call.value.FacilityID = call.value.FacilityID || EMPTY_GUID
  if (!outcome.value) {
    outcome.value = toScreenOutcome(call.value.CallOutcome || DEFAULT_OUTCOME)
  }
}

const storeAsync = async () => {
  // this screen only owns relatedDeviceIds - merge onto whatever else was last loaded so other
  // properties a different screen may add under other keys aren't wiped out
  call.value.Properties = {
    ...savedProperties.value,
    relatedDeviceIds: devices.value.filter((d) => d.isRelated).map((d) => d.id),
  }
  return await saveEntityAsync(fetchAuthAsync, `Calls/${call.value.ID}`, call.value, false)
}

const saveAsync = async () => {
  if (saving.value || !call.value || !isValid.value) return
  saving.value = true
  try {
    if (!(await storeAsync())) return

    if (canApplyOutcome.value) {
      const response = await setCallOutcomeAsync(
        fetchAuthAsync,
        call.value.ID,
        outcome.value,
        needsLinkedSdn.value ? call.value.ServiceOrderID : "",
      )
      if (!response) {
        return
      }
      setSuccess(response.orderNumber ? `Ustvarjeno: ${response.orderNumber}` : "Razlog klica je shranjen.")
      if (outcome.value === "Attention" && response.serviceOrderID) {
        router.push({ name: "ServiceOrder", params: { workOrderId: response.serviceOrderID } })
        return
      }
    }
    await loadAsync()
  } catch (e) {
    setError(e)
  } finally {
    saving.value = false
  }
}

// one of the two "Alarm" buttons - records AlertSolved or Alert (new SDN) once; after that the
// other button is gone and neither can be pressed again
const applyAlarmAsync = async (alarmOutcome) => {
  if (saving.value || !call.value || !isValid.value || recordedAlarm.value) return
  saving.value = true
  try {
    if (!(await storeAsync())) return

    const response = await setCallOutcomeAsync(fetchAuthAsync, call.value.ID, alarmOutcome, "")
    if (!response) return
    setSuccess(response.orderNumber ? `Ustvarjeno: ${response.orderNumber}` : "Razlog klica je shranjen.")
    if (alarmOutcome === "Alert" && response.serviceOrderID) {
      router.push({ name: "ServiceOrder", params: { workOrderId: response.serviceOrderID } })
      return
    }
    await loadAsync()
  } catch (e) {
    setError(e)
  } finally {
    saving.value = false
  }
}

// "Users" - the switch goes straight to the device, and the device is tied to the call for good:
// the relation is stored right away so it survives leaving without saving, and there is no way
// back to unrelated from this list
const toggleDefrostingAsync = async (device) => {
  if (saving.value || !call.value || !device?.id) return
  saving.value = true
  try {
    const next = !device.isDefrosting
    if (!(await setDeviceDefrostingAsync(fetchAuthAsync, device.id, next))) return
    device.isDefrosting = next
    if (!next) device.defrostingNote = ""
    const wasRelated = device.isRelated
    device.isRelated = true
    if (!wasRelated) await storeAsync()
    setSuccess(`${device.label}: odtaljevanje ${next ? "vklopljeno" : "izklopljeno"}.`)
  } catch (e) {
    setError(e)
  } finally {
    saving.value = false
  }
}

const linkOrderAsync = async (order) => {
  if (saving.value || !call.value || !order?.id) return
  saving.value = true
  try {
    if (!(await storeAsync())) return

    const response = await setCallOutcomeAsync(fetchAuthAsync, call.value.ID, "ExistingSdn", order.id)
    if (!response) return
    if (outcome.value !== "Complaint") {
      outcome.value = "ExistingSdn"
    }
    setSuccess(`Povezan SDN ${response.orderNumber || ""}.`)
    await loadAsync()
  } catch (e) {
    setError(e)
  } finally {
    saving.value = false
  }
}

const unlinkOrderAsync = async () => {
  if (saving.value || !call.value || !hasLinkedSdn.value) return
  saving.value = true
  try {
    const response = await unlinkCallOutcomeAsync(fetchAuthAsync, call.value.ID)
    if (!response) return
    // matches what CallUnlinkOutcome actually put back on the row (see api/Calls.cs)
    outcome.value = "Tbd"
    setSuccess("Povezava z SDN je odstranjena.")
    await loadAsync()
  } catch (e) {
    setError(e)
  } finally {
    saving.value = false
  }
}

const selectDevice = (match) => {
  if (!call.value) return
  call.value.CustomerID = match.customerID || EMPTY_GUID
  call.value.FacilityID = match.facilityID || EMPTY_GUID
  // the number only says WHERE the call comes from - the selects above carry the customer/object
  facilityInfo.value = `Objekt: ${match.facilityName || "?"} (${match.deviceNumber})`
}

const onDeviceNotFound = (ident) => {
  facilityInfo.value = `Ni naprave s šifro "${ident}".`
}

watch(
  () => route.params.callId,
  () => {
    outcome.value = ""
    facilityInfo.value = ""
    devices.value = []
    loadAsync()
  },
)

onMounted(async () => {
  try {
    await loadAsync()
  } catch (e) {
    setError(e)
  }
})
</script>
